/**
 * 配置管理工具的辅助函数模块.
 *
 * 提供常用的值转换函数，用于配置项的值处理。
 * 可以通过konph.helper.git访问这些工具函数。
 *
 * @packageDocumentation
 * @author yusangeng@outlook.com
 */
import isNumber from "lodash/isNumber";
import isBoolean from "lodash/isBoolean";
import isUndefined from "lodash/isUndefined";
var isArray = Array.isArray;
/**
 * 帮助函数, 可以通过Konph.helper访问.
 *
 * @namespace
 */
var helper = {
    /**
     * 常用fit函数.
     *
     * @example
     * fit提供的方法常用来简化配置中fit函数的书写, 比如:
     *
     * {
     *   foobar: {
     *     def: true,
     *     fit: konph.helper.fit.boolean // 将任何值转换为布尔值
     *   }
     * }
     *
     * @namespace
     */
    fit: {
        /**
         * 转换为布尔值.
         *
         * 字符串`false`和`0`会转换为false, 其他值转换为!!value.
         *
         * @param {any} value 输入值.
         * @returns {boolean} 转换结果.
         */
        boolean: function (value) {
            if (value === "false" || value === "0") {
                return false;
            }
            return !!value;
        },
        // 兼容老版本
        bool: function (value) {
            return helper.fit.boolean(value);
        },
        /**
         * 转换为数字类型.
         *
         * 如果是数字则直接返回, 如果是布尔值或可parseFloat的字符串会转换为number, 其余转换为NaN.
         *
         * @param {any} value 输入值.
         * @returns {number} 转换结果.
         */
        number: function (value) {
            if (typeof value === "string") {
                if (value === "true") {
                    return 1;
                }
                else if (value === "false") {
                    return 0;
                }
                var n = parseFloat(value);
                if (isNaN(n)) {
                    console.warn("".concat(value, " is NOT a number."));
                    return NaN;
                }
                return n;
            }
            if (isNumber(value)) {
                return value;
            }
            if (isBoolean(value)) {
                return value ? 1 : 0;
            }
            return NaN;
        },
        /**
         * 转换为字符串列表.
         *
         * 如果是数组直接返回, 如果是字符串, 执行以下操作:
         *   1. 首先trim.
         *   2. 去掉开头的`[`和结尾的`]`.
         *   3. 用`,`分割.
         *   4. 对分割的每一项trim.
         *   5. 删除为空字符串的项.
         *   6. 将得到的数组返回.
         *
         * 如果是undefined, 返回空数组, 如果是其他值, 则包装成一个数组返回.
         *
         * @param {any} value 输入值.
         * @returns {Array} 转换结果.
         */
        strings: function (value) {
            if (isArray(value)) {
                return value.map(function (el) { return "" + el; });
            }
            if (isUndefined(value)) {
                return [];
            }
            if (typeof value === "string") {
                var v = value.trim().replace(/^\[|\]$/g, "").trim();
                var ret = v
                    .split(",")
                    .map(function (el) { return el.trim(); })
                    .filter(function (el) { return el !== ""; });
                return ret;
            }
            return [value];
        },
        // 兼容老版本
        array: function (value) {
            return helper.fit.strings(value);
        }
    }
};
export default helper;
//# sourceMappingURL=data:application/json;base64,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